import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, keyframes, styled } from "@mui/material/styles";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import LibraryBooksRoundedIcon from "@mui/icons-material/LibraryBooksRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import StarRoundedIcon from "@mui/icons-material/StarRounded";

const MotionBox = motion(Box);

const BRAND = {
  turquoise: "#00B4A6",
  turquoiseLight: "#5FF5EA",
  turquoiseDark: "#00796B",
  turquoiseSoft: "#DDFBF8",
  gold: "#D4AF37",
  goldLight: "#FFE08A",
  ink: "#071313",
  deep: "#031817",
  muted: "#637D7A",
  border: "#D8EFEB",
  card: "#FFFFFF",
  bg: "#F8FFFD",
  subtle: "#F3FFFC",
};

const HEADING_FONT = "'Space Grotesk', 'Inter', system-ui, sans-serif";
const BODY_FONT = "'Inter', 'Segoe UI', system-ui, sans-serif";
const MONO_FONT = "'JetBrains Mono', 'Fira Code', monospace";

const CREATOR_RADAR_ENDPOINT = "/api/creator-radar/";
const LEGACY_FEED_ENDPOINT = "/api/opportunities-feed/";
const SUBSCRIPTION_ENDPOINT = "/api/opportunity-subscriptions/";

const SITE_NAME = "Art For My Room";
const SITE_URL = "https://artformyroom.com";
const SITE_EMAIL = "info@artformyroom.com";

const topicOptions = [
  "All",
  "Artist Marketing",
  "Selling Art",
  "Room Styling",
  "Pricing",
  "Photography",
  "AI Tools",
  "Creative Business",
  "Exhibitions",
  "Grants",
  "Collector Behaviour",
];

const sortOptions = [
  { value: "newest", label: "Newest first" },
  { value: "useful", label: "Most useful" },
  { value: "topic", label: "Topic A-Z" },
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(isoDate) {
  if (!isoDate) return "Today";

  const d = new Date(`${isoDate}T00:00:00`);

  if (Number.isNaN(d.getTime())) return "Today";

  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatDateShort(isoDate) {
  if (!isoDate) return "Today";

  const d = new Date(`${isoDate}T00:00:00`);

  if (Number.isNaN(d.getTime())) return "Today";

  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(value) {
  if (!value) return null;

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) return null;

  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normaliseText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/https?:\/\//g, "")
    .replace(/www\./g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function normaliseUrl(value) {
  if (!value) return "";

  try {
    const url = new URL(value);
    return `${url.hostname.replace(/^www\./, "")}${url.pathname}`
      .replace(/\/+$/, "")
      .toLowerCase();
  } catch {
    return normaliseText(value);
  }
}

function slugify(value) {
  const slug = normaliseText(value).replace(/\s+/g, "-");
  return slug || "creator-radar";
}

function normaliseArray(value) {
  if (Array.isArray(value)) return value.filter(Boolean);

  if (typeof value === "string" && value.trim()) {
    return value
      .split(/[,|]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function makeRadarKey(item) {
  const sourceUrl = item.sourceUrl || item.url || item.articleUrl || "";
  const urlKey = normaliseUrl(sourceUrl);
  const titleKey = normaliseText(item.title);

  if (urlKey && titleKey) return `${urlKey}::${titleKey}`;
  return urlKey || titleKey || item.id || item.slug || Math.random().toString(36);
}

function dedupeRadarItems(items) {
  const map = new Map();
  let duplicatesRemoved = 0;

  items.forEach((rawItem, index) => {
    const item = normaliseRadarItem(rawItem, index);
    const key = makeRadarKey(item);

    if (!map.has(key)) {
      map.set(key, item);
      return;
    }

    duplicatesRemoved += 1;

    const existing = map.get(key);
    const existingScore = Number(existing.usefulnessScore || 0);
    const itemScore = Number(item.usefulnessScore || 0);
    const existingDate = new Date(existing.date || "1970-01-01").getTime();
    const itemDate = new Date(item.date || "1970-01-01").getTime();

    const shouldReplace =
      itemScore > existingScore ||
      itemDate > existingDate ||
      (item.summary || "").length > (existing.summary || "").length;

    if (shouldReplace) {
      map.set(key, item);
    }
  });

  return {
    items: Array.from(map.values()),
    duplicatesRemoved,
  };
}

function getReadingTime(value) {
  if (!value) return "3 min read";

  const clean = String(value).trim();

  if (clean.toLowerCase().includes("min")) return clean;

  const number = Number(clean.match(/\d+/)?.[0] || 3);
  return `${number} min read`;
}

function normaliseSourceLinks(rawItem) {
  const links = [];

  const possibleLinks = [
    ...(Array.isArray(rawItem.sourceLinks) ? rawItem.sourceLinks : []),
    ...(Array.isArray(rawItem.sources) ? rawItem.sources : []),
    ...(Array.isArray(rawItem.references) ? rawItem.references : []),
  ];

  possibleLinks.forEach((source, index) => {
    if (typeof source === "string") {
      links.push({
        title: `Source ${index + 1}`,
        url: source,
        publisher: "",
      });
      return;
    }

    if (source && typeof source === "object") {
      links.push({
        title: source.title || source.name || source.publisher || `Source ${index + 1}`,
        url: source.url || source.link || source.href || "",
        publisher: source.publisher || source.source || "",
      });
    }
  });

  const singleUrl =
    rawItem.sourceUrl ||
    rawItem.url ||
    rawItem.articleUrl ||
    rawItem.link ||
    rawItem.original_url ||
    "";

  if (singleUrl) {
    const alreadyIncluded = links.some(
      (item) => normaliseUrl(item.url) === normaliseUrl(singleUrl)
    );

    if (!alreadyIncluded) {
      links.unshift({
        title: rawItem.sourceTitle || rawItem.source || rawItem.publisher || "Original article",
        url: singleUrl,
        publisher: rawItem.publisher || rawItem.source || "",
      });
    }
  }

  return links.filter((item) => item.url);
}

function normaliseRadarItem(rawItem, index = 0) {
  const title =
    rawItem.title ||
    rawItem.headline ||
    rawItem.articleTitle ||
    rawItem.name ||
    `Creator Radar insight ${index + 1}`;

  const topic =
    rawItem.topic ||
    rawItem.category ||
    rawItem.primaryTopic ||
    rawItem.type ||
    "Artist Marketing";

  const date =
    rawItem.date ||
    rawItem.publishedDate ||
    rawItem.published_date ||
    rawItem.created_at ||
    todayIso();

  const summary =
    rawItem.summary ||
    rawItem.shortSummary ||
    rawItem.artistSummary ||
    rawItem.description ||
    rawItem.excerpt ||
    "A short artist-focused summary will appear here when the daily radar feed is connected.";

  const seoTitle =
    rawItem.seoTitle ||
    rawItem.metaTitle ||
    `${title} | Creator Radar | ${SITE_NAME}`;

  const seoDescription =
    rawItem.seoDescription ||
    rawItem.metaDescription ||
    `${summary}`.slice(0, 155);

  const importantPoints = normaliseArray(
    rawItem.importantPoints ||
      rawItem.keyPoints ||
      rawItem.mainPoints ||
      rawItem.artistTakeaways ||
      rawItem.takeaways
  );

  const artistActions = normaliseArray(
    rawItem.artistActions ||
      rawItem.actions ||
      rawItem.nextSteps ||
      rawItem.practicalSteps
  );

  const keywords = normaliseArray(
    rawItem.keywords ||
      rawItem.seoKeywords ||
      rawItem.tags ||
      rawItem.categories
  );

  const sourceLinks = normaliseSourceLinks(rawItem);

  const sourceUrl = sourceLinks[0]?.url || rawItem.url || rawItem.sourceUrl || SITE_URL;

  return {
    id: rawItem.id || slugify(`${date}-${title}`),
    slug: rawItem.slug || slugify(title),
    date,
    title,
    seoTitle,
    seoDescription,
    topic,
    summary,
    importantPoints:
      importantPoints.length > 0
        ? importantPoints
        : [
            "Look at how the article connects audience, trust and visibility.",
            "Use the idea to improve how your painting is presented online.",
            "Turn one insight into a small action today.",
          ],
    artistActions:
      artistActions.length > 0
        ? artistActions
        : [
            "Update one artwork description.",
            "Improve one painting photo.",
            "Add one clearer buyer-facing detail to your artist page.",
          ],
    keywords:
      keywords.length > 0
        ? keywords
        : [
            "artist marketing",
            "selling art online",
            "painting presentation",
            "art buyers",
            "room matching art",
          ],
    readTime: getReadingTime(rawItem.readTime || rawItem.read_time || rawItem.readingTime),
    source: rawItem.source || rawItem.publisher || sourceLinks[0]?.publisher || "External article",
    sourceUrl,
    sourceLinks,
    usefulnessScore: Math.max(
      1,
      Math.min(100, Number(rawItem.usefulnessScore || rawItem.score || rawItem.priority || 88))
    ),
    imageUrl: rawItem.imageUrl || rawItem.image_url || rawItem.thumbnail || "",
    isFallback: Boolean(rawItem.isFallback),
  };
}

const fallbackDailyRadar = {
  id: "creator-radar-start-fresh-today",
  date: todayIso(),
  title: "How Artists Can Make One Painting Easier for Buyers to Imagine at Home",
  topic: "Selling Art",
  source: "Art For My Room editorial note",
  sourceUrl: `${SITE_URL}/artist-room-matcher`,
  seoTitle:
    "How Artists Can Make One Painting Easier for Buyers to Imagine at Home | Creator Radar",
  seoDescription:
    "Daily Creator Radar summary for painters: practical ways to present one painting so buyers can imagine it in a real room.",
  summary:
    "Today’s Creator Radar focuses on a simple but powerful idea: buyers do not only buy a painting, they buy the feeling of where it could belong. Artists can improve trust and interest by showing clear photos, accurate size, room-friendly colours, a short story, and practical details that help the buyer imagine the artwork on their own wall.",
  importantPoints: [
    "A painting becomes easier to buy when the buyer can picture it inside a real room, not only as a flat image.",
    "Clear size information matters. Width and height in centimetres help buyers understand whether the work can fill an empty wall nicely.",
    "Colour language helps discovery. Mention the dominant colours, mood, and room styles the painting could suit.",
    "A short story gives the artwork emotional context, but it should stay useful and readable rather than becoming too abstract.",
    "Local discovery can increase confidence because buyers may feel closer to the artist and worry less about delivery.",
  ],
  artistActions: [
    "Choose one painting and rewrite its description with colour, mood, size and room suitability.",
    "Photograph the painting straight-on in natural light, then add one close-up detail shot.",
    "Add the painting width, height, country and price before sharing it online.",
    "Use Art For My Room to test how the painting feels inside a room photo.",
  ],
  keywords: [
    "sell paintings online",
    "art for living room",
    "artist marketing",
    "painting description",
    "room matching art",
    "original paintings UK",
  ],
  readTime: "4 min read",
  usefulnessScore: 95,
  isFallback: true,
};

const fall = keyframes`
  0% { transform: translateY(-12px); opacity: 0; }
  10% { opacity: 0.55; }
  100% { transform: translateY(120vh); opacity: 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-12px) rotate(1deg); }
`;

const shimmer = keyframes`
  0% { transform: translateX(-130%); }
  100% { transform: translateX(130%); }
`;

const PageShell = styled(Box)({
  minHeight: "100vh",
  background:
    `radial-gradient(circle at 10% 8%, ${alpha(BRAND.turquoise, 0.20)}, transparent 32%),
     radial-gradient(circle at 88% 12%, ${alpha(BRAND.gold, 0.20)}, transparent 30%),
     linear-gradient(180deg, #F8FFFD 0%, #ECFFFC 42%, #FFF9EC 100%)`,
  fontFamily: BODY_FONT,
  color: BRAND.ink,
  position: "relative",
  overflow: "hidden",
});

const DotsLayer = styled(Box)({
  position: "fixed",
  inset: 0,
  pointerEvents: "none",
  zIndex: 0,
  overflow: "hidden",
});

const Dot = styled(Box, {
  shouldForwardProp: (prop) =>
    !["dotLeft", "dotSize", "dotDelay", "dotDuration", "dotColor"].includes(prop),
})(({ dotLeft, dotSize, dotDelay, dotDuration, dotColor }) => ({
  position: "absolute",
  top: -40,
  left: dotLeft,
  width: dotSize,
  height: dotSize,
  borderRadius: "999px",
  background: dotColor,
  opacity: 0.24,
  filter: "blur(0.5px)",
  animation: `${fall} ${dotDuration}s linear infinite`,
  animationDelay: `${dotDelay}s`,
}));

const PageContent = styled(Box)({
  position: "relative",
  zIndex: 2,
});

const HeroShell = styled(Paper)({
  position: "relative",
  overflow: "hidden",
  borderRadius: "42px",
  color: "#FFFFFF",
  minHeight: "500px",
  background:
    `radial-gradient(circle at 14% 16%, ${alpha(BRAND.turquoiseLight, 0.32)}, transparent 30%),
     radial-gradient(circle at 86% 16%, ${alpha(BRAND.goldLight, 0.26)}, transparent 26%),
     linear-gradient(135deg, #031817 0%, #07312F 48%, #2F2508 100%)`,
  boxShadow: "0 42px 110px rgba(0,80,74,0.26)",
  border: `1px solid ${alpha("#FFFFFF", 0.16)}`,
});

const HeroGlow = styled(Box)({
  position: "absolute",
  borderRadius: "999px",
  filter: "blur(5px)",
  opacity: 0.55,
  animation: `${float} 7s ease-in-out infinite`,
});

const HeroPattern = styled(Box)({
  position: "absolute",
  inset: 0,
  opacity: 0.14,
  backgroundImage:
    `linear-gradient(${alpha("#FFFFFF", 0.22)} 1px, transparent 1px),
     linear-gradient(90deg, ${alpha("#FFFFFF", 0.22)} 1px, transparent 1px)`,
  backgroundSize: "34px 34px",
  mixBlendMode: "screen",
});

const GlassCard = styled(Paper)({
  background: alpha("#FFFFFF", 0.84),
  backdropFilter: "blur(22px)",
  border: `1px solid ${alpha("#FFFFFF", 0.74)}`,
  borderRadius: "30px",
  boxShadow: "0 28px 80px rgba(0,80,74,0.12)",
  overflow: "hidden",
});

const DarkCard = styled(Paper)({
  background:
    `radial-gradient(circle at 12% 14%, ${alpha(BRAND.turquoise, 0.20)}, transparent 34%),
     radial-gradient(circle at 92% 18%, ${alpha(BRAND.gold, 0.18)}, transparent 32%),
     linear-gradient(135deg, #041918 0%, #061F1E 55%, #2B2107 100%)`,
  color: "#FFFFFF",
  borderRadius: "30px",
  border: `1px solid ${alpha("#FFFFFF", 0.14)}`,
  boxShadow: "0 30px 90px rgba(0,0,0,0.22)",
  overflow: "hidden",
});

const GradientButton = styled(Button)({
  fontFamily: BODY_FONT,
  fontWeight: 900,
  textTransform: "none",
  borderRadius: "999px",
  padding: "13px 25px",
  background: `linear-gradient(135deg, ${BRAND.turquoise} 0%, ${BRAND.gold} 100%)`,
  color: "#FFFFFF",
  boxShadow: `0 16px 36px ${alpha(BRAND.turquoise, 0.26)}`,
  "&:hover": {
    background: `linear-gradient(135deg, ${BRAND.turquoiseDark} 0%, #B99325 100%)`,
    transform: "translateY(-2px)",
    boxShadow: `0 20px 44px ${alpha(BRAND.gold, 0.25)}`,
  },
});

const SoftButton = styled(Button)({
  fontFamily: BODY_FONT,
  fontWeight: 850,
  textTransform: "none",
  borderRadius: "999px",
  color: BRAND.turquoiseDark,
  border: `1.5px solid ${alpha(BRAND.turquoise, 0.34)}`,
  background: alpha("#FFFFFF", 0.78),
  "&:hover": {
    background: BRAND.turquoiseSoft,
    borderColor: BRAND.turquoise,
  },
});

const Field = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "18px",
    background: alpha("#FFFFFF", 0.92),
  },
  "& label.Mui-focused": {
    color: BRAND.turquoiseDark,
  },
});

const ArticleVisual = styled(Box)({
  minHeight: 245,
  borderRadius: "28px",
  position: "relative",
  overflow: "hidden",
  background:
    `radial-gradient(circle at 18% 22%, ${alpha(BRAND.turquoiseLight, 0.75)}, transparent 30%),
     radial-gradient(circle at 82% 26%, ${alpha(BRAND.goldLight, 0.70)}, transparent 26%),
     linear-gradient(135deg, #06201E 0%, #09433F 58%, #6B5412 100%)`,
  border: `1px solid ${alpha("#FFFFFF", 0.2)}`,
  boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
  "&:before": {
    content: '""',
    position: "absolute",
    inset: 0,
    backgroundImage:
      `linear-gradient(${alpha("#FFFFFF", 0.20)} 1px, transparent 1px),
       linear-gradient(90deg, ${alpha("#FFFFFF", 0.20)} 1px, transparent 1px)`,
    backgroundSize: "30px 30px",
    opacity: 0.28,
  },
  "&:after": {
    content: '""',
    position: "absolute",
    top: 0,
    bottom: 0,
    width: "42%",
    left: "-50%",
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)",
    animation: `${shimmer} 5s ease-in-out infinite`,
  },
});

function BackgroundDots() {
  const dots = useMemo(() => {
    const colours = [
      alpha(BRAND.turquoise, 0.8),
      alpha(BRAND.turquoiseLight, 0.75),
      alpha(BRAND.gold, 0.72),
      alpha(BRAND.goldLight, 0.74),
    ];

    return Array.from({ length: 46 }).map((_, index) => ({
      id: index,
      left: `${Math.random() * 100}%`,
      size: `${Math.floor(Math.random() * 7) + 4}px`,
      delay: Math.random() * 12,
      duration: Math.random() * 12 + 16,
      color: colours[index % colours.length],
    }));
  }, []);

  return (
    <DotsLayer>
      {dots.map((dot) => (
        <Dot
          key={dot.id}
          dotLeft={dot.left}
          dotSize={dot.size}
          dotDelay={dot.delay}
          dotDuration={dot.duration}
          dotColor={dot.color}
        />
      ))}
    </DotsLayer>
  );
}

function updateMetaTag(name, content) {
  if (!content) return;

  let tag = document.querySelector(`meta[name="${name}"]`);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
}

function updatePropertyTag(property, content) {
  if (!content) return;

  let tag = document.querySelector(`meta[property="${property}"]`);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
}

function updateCanonical(url) {
  let link = document.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", url);
}

function updateJsonLd(radar) {
  const id = "creator-radar-jsonld";
  let script = document.getElementById(id);

  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  const canonicalUrl = `${SITE_URL}/creator-radar/${radar.date}/${radar.slug}`;

  script.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: radar.title,
    description: radar.seoDescription || radar.summary,
    datePublished: radar.date,
    dateModified: radar.date,
    mainEntityOfPage: canonicalUrl,
    author: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
    keywords: radar.keywords.join(", "),
  });
}

function SourceLinks({ item }) {
  const links = item.sourceLinks || [];

  if (!links.length) {
    return (
      <Button
        href={item.sourceUrl}
        target="_blank"
        rel="noreferrer"
        endIcon={<LaunchRoundedIcon />}
        sx={{
          textTransform: "none",
          fontWeight: 850,
          color: BRAND.turquoiseDark,
        }}
      >
        Open source article
      </Button>
    );
  }

  return (
    <Stack spacing={1}>
      <Typography sx={{ fontWeight: 950, color: BRAND.ink }}>
        Sources and further reading
      </Typography>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {links.map((source, index) => (
          <Button
            key={`${source.url}-${index}`}
            href={source.url}
            target="_blank"
            rel="noreferrer"
            size="small"
            variant="outlined"
            startIcon={<LinkRoundedIcon />}
            endIcon={<LaunchRoundedIcon />}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              fontWeight: 850,
              color: BRAND.turquoiseDark,
              borderColor: alpha(BRAND.turquoise, 0.35),
              backgroundColor: alpha("#FFFFFF", 0.72),
            }}
          >
            {source.title || `Source ${index + 1}`}
          </Button>
        ))}
      </Stack>
    </Stack>
  );
}

function RadarArticleDetail({ item, featured = false }) {
  if (!item) return null;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: featured ? 5 : 4,
        border: featured
          ? `1px solid ${alpha(BRAND.gold, 0.38)}`
          : `1px solid ${BRAND.border}`,
        background: featured
          ? `linear-gradient(180deg, #FFFFFF 0%, ${alpha(BRAND.turquoiseSoft, 0.58)} 100%)`
          : BRAND.card,
        boxShadow: featured
          ? "0 28px 76px rgba(0,80,74,0.16)"
          : "0 16px 42px rgba(20, 51, 58, 0.06)",
      }}
    >
      <CardContent sx={{ p: featured ? { xs: 3, md: 4 } : 3 }}>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip
            icon={<AutoAwesomeIcon />}
            label={featured ? "Best article of the day" : "Archive summary"}
            sx={{
              background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.gold})`,
              color: "#FFFFFF",
              fontWeight: 950,
              "& .MuiChip-icon": { color: "#FFFFFF" },
            }}
          />

          <Chip
            icon={<CalendarMonthRoundedIcon />}
            label={formatDate(item.date)}
            sx={{
              backgroundColor: alpha(BRAND.gold, 0.12),
              color: "#7A5B00",
              fontWeight: 850,
            }}
          />

          <Chip
            icon={<AccessTimeRoundedIcon />}
            label={item.readTime}
            sx={{
              backgroundColor: "#F7FBFB",
              color: BRAND.muted,
              fontWeight: 850,
            }}
          />

          <Chip
            icon={<LocalOfferOutlinedIcon />}
            label={item.topic}
            sx={{
              backgroundColor: BRAND.turquoiseSoft,
              color: BRAND.turquoiseDark,
              fontWeight: 850,
            }}
          />
        </Stack>

        <Typography
          sx={{
            fontFamily: HEADING_FONT,
            fontWeight: 950,
            lineHeight: 1.05,
            letterSpacing: "-0.055em",
            color: BRAND.ink,
            fontSize: featured ? { xs: "2rem", md: "3.25rem" } : { xs: "1.65rem", md: "2.2rem" },
            mb: 2,
          }}
        >
          {item.title}
        </Typography>

        <Typography
          sx={{
            color: BRAND.muted,
            lineHeight: 1.9,
            fontSize: featured ? { xs: "1rem", md: "1.09rem" } : "1rem",
            mb: 3,
          }}
        >
          {item.summary}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 4,
                backgroundColor: "#FBFEFD",
                border: `1px solid ${alpha(BRAND.turquoise, 0.18)}`,
                height: "100%",
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.4 }}>
                <TipsAndUpdatesRoundedIcon sx={{ color: BRAND.turquoiseDark }} />
                <Typography sx={{ fontWeight: 950, color: BRAND.ink }}>
                  Important points for artists
                </Typography>
              </Stack>

              <Stack spacing={1.3}>
                {item.importantPoints.map((point, index) => (
                  <Stack key={`${point}-${index}`} direction="row" spacing={1.2} alignItems="flex-start">
                    <CheckCircleRoundedIcon
                      sx={{ color: BRAND.turquoiseDark, fontSize: 21, mt: "2px" }}
                    />
                    <Typography sx={{ color: BRAND.muted, lineHeight: 1.75 }}>
                      {point}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 4,
                backgroundColor: alpha(BRAND.gold, 0.08),
                border: `1px solid ${alpha(BRAND.gold, 0.20)}`,
                height: "100%",
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.4 }}>
                <StarRoundedIcon sx={{ color: "#9B7300" }} />
                <Typography sx={{ fontWeight: 950, color: BRAND.ink }}>
                  What to do today
                </Typography>
              </Stack>

              <Stack spacing={1.3}>
                {item.artistActions.map((action, index) => (
                  <Stack key={`${action}-${index}`} direction="row" spacing={1.2} alignItems="flex-start">
                    <VerifiedRoundedIcon
                      sx={{ color: "#9B7300", fontSize: 21, mt: "2px" }}
                    />
                    <Typography sx={{ color: BRAND.muted, lineHeight: 1.75 }}>
                      {action}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: BRAND.border }} />

        <Stack spacing={2}>
          <Box>
            <Typography sx={{ fontWeight: 950, color: BRAND.ink, mb: 1 }}>
              SEO keywords for today
            </Typography>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {item.keywords.map((keyword) => (
                <Chip
                  key={keyword}
                  label={keyword}
                  size="small"
                  sx={{
                    backgroundColor: alpha(BRAND.ink, 0.05),
                    color: BRAND.ink,
                    fontWeight: 750,
                  }}
                />
              ))}
            </Stack>
          </Box>

          <SourceLinks item={item} />
        </Stack>
      </CardContent>
    </Card>
  );
}

function ArchiveItem({ item, active, onClick }) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2,
        borderRadius: 4,
        cursor: "pointer",
        background: active
          ? `linear-gradient(135deg, ${alpha(BRAND.turquoise, 0.12)}, ${alpha(BRAND.gold, 0.12)})`
          : alpha("#FFFFFF", 0.78),
        border: active
          ? `1px solid ${alpha(BRAND.turquoiseDark, 0.34)}`
          : `1px solid ${BRAND.border}`,
        transition: "all 0.22s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 16px 34px rgba(0,80,74,0.10)",
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "16px",
            display: "grid",
            placeItems: "center",
            color: active ? "#FFFFFF" : BRAND.turquoiseDark,
            background: active
              ? `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.gold})`
              : BRAND.turquoiseSoft,
            flexShrink: 0,
          }}
        >
          <ArticleOutlinedIcon />
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 950, color: BRAND.ink, lineHeight: 1.25 }}>
            {item.title}
          </Typography>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.8 }}>
            <Chip
              size="small"
              label={formatDateShort(item.date)}
              sx={{ height: 22, fontSize: "0.72rem" }}
            />
            <Chip
              size="small"
              label={item.topic}
              sx={{
                height: 22,
                fontSize: "0.72rem",
                backgroundColor: BRAND.turquoiseSoft,
                color: BRAND.turquoiseDark,
              }}
            />
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function CreativeTechOpportunitiesPage() {
  const [dailyRadar, setDailyRadar] = useState(normaliseRadarItem(fallbackDailyRadar));
  const [archiveItems, setArchiveItems] = useState([]);
  const [selectedArchiveItem, setSelectedArchiveItem] = useState(null);
  const [feedUpdatedAt, setFeedUpdatedAt] = useState(null);
  const [feedLoading, setFeedLoading] = useState(true);
  const [feedMessage, setFeedMessage] = useState("");
  const [duplicatesRemoved, setDuplicatesRemoved] = useState(0);

  const [filters, setFilters] = useState({
    search: "",
    topic: "All",
  });
  const [sortBy, setSortBy] = useState("newest");

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    artistName: "",
    topic: "Selling Art",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    let ignore = false;

    const fetchJson = async (url) => {
      const response = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
      });

      let data = null;
      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { message: text || "Unexpected feed response." };
      }

      if (!response.ok) {
        throw new Error(data.message || `Feed request failed: ${response.status}`);
      }

      return data;
    };

    const loadCreatorRadar = async () => {
      try {
        if (!ignore) setFeedLoading(true);

        let data = null;

        try {
          data = await fetchJson(CREATOR_RADAR_ENDPOINT);
        } catch (creatorRadarError) {
          data = await fetchJson(LEGACY_FEED_ENDPOINT);
        }

        const rawDaily =
          data.daily_article ||
          data.dailyArticle ||
          data.today_article ||
          data.todayArticle ||
          data.current ||
          data.main ||
          data.featured ||
          null;

        const rawArchive = [
          ...(Array.isArray(data.archive) ? data.archive : []),
          ...(Array.isArray(data.daily_articles) ? data.daily_articles : []),
          ...(Array.isArray(data.articles) ? data.articles : []),
          ...(Array.isArray(data.items) ? data.items : []),
          ...(Array.isArray(data.opportunities) ? data.opportunities : []),
        ];

        const daily = normaliseRadarItem(rawDaily || fallbackDailyRadar, 0);
        const archiveSource = rawArchive.length ? rawArchive : [];

        const dedupedArchive = dedupeRadarItems(
          archiveSource.filter((item) => makeRadarKey(normaliseRadarItem(item)) !== makeRadarKey(daily))
        );

        if (!ignore) {
          setDailyRadar(daily);
          setArchiveItems(dedupedArchive.items);
          setDuplicatesRemoved(dedupedArchive.duplicatesRemoved);
          setFeedUpdatedAt(data.updated_at || data.updatedAt || null);
          setFeedMessage(
            rawDaily
              ? ""
              : "Creator Radar backend did not return today's article yet, so the page is showing today's starter editorial."
          );
        }
      } catch (error) {
        console.error("Could not load Creator Radar feed:", error);

        if (!ignore) {
          setDailyRadar(normaliseRadarItem(fallbackDailyRadar, 0));
          setArchiveItems([]);
          setDuplicatesRemoved(0);
          setFeedMessage("Creator Radar feed unavailable right now. Showing today's starter editorial.");
        }
      } finally {
        if (!ignore) setFeedLoading(false);
      }
    };

    loadCreatorRadar();
    const intervalId = setInterval(loadCreatorRadar, 10 * 60 * 1000);

    return () => {
      ignore = true;
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const pageTitle = dailyRadar.seoTitle || `${dailyRadar.title} | Creator Radar | ${SITE_NAME}`;
    const pageDescription =
      dailyRadar.seoDescription ||
      `${dailyRadar.summary}`.slice(0, 155) ||
      "Daily Creator Radar summary for artists and painters from Art For My Room.";

    const canonicalUrl = `${SITE_URL}/creator-radar/${dailyRadar.date}/${dailyRadar.slug}`;

    document.title = pageTitle;
    updateMetaTag("description", pageDescription);
    updateMetaTag("keywords", dailyRadar.keywords.join(", "));
    updateMetaTag("robots", "index, follow");
    updatePropertyTag("og:title", pageTitle);
    updatePropertyTag("og:description", pageDescription);
    updatePropertyTag("og:type", "article");
    updatePropertyTag("og:url", canonicalUrl);
    updateCanonical(canonicalUrl);
    updateJsonLd(dailyRadar);
  }, [dailyRadar]);

  const filteredArchive = useMemo(() => {
    const query = filters.search.trim().toLowerCase();

    let items = archiveItems.filter((item) => {
      const haystack = [
        item.title,
        item.summary,
        item.topic,
        item.source,
        ...item.keywords,
        ...item.importantPoints,
      ]
        .join(" ")
        .toLowerCase();

      const searchOk = !query || haystack.includes(query);
      const topicOk = filters.topic === "All" || item.topic === filters.topic;

      return searchOk && topicOk;
    });

    if (sortBy === "useful") {
      items = [...items].sort((a, b) => b.usefulnessScore - a.usefulnessScore);
    } else if (sortBy === "topic") {
      items = [...items].sort((a, b) => a.topic.localeCompare(b.topic));
    } else {
      items = [...items].sort((a, b) => {
        const aDate = new Date(a.date || "1970-01-01").getTime();
        const bDate = new Date(b.date || "1970-01-01").getTime();
        return bDate - aDate;
      });
    }

    return items;
  }, [archiveItems, filters, sortBy]);

  const archiveDetail = selectedArchiveItem || null;

  const stats = useMemo(() => {
    const allItems = [dailyRadar, ...archiveItems];

    return {
      archive: archiveItems.length,
      keywords: new Set(allItems.flatMap((item) => item.keywords || [])).size,
      sources: new Set(allItems.flatMap((item) => (item.sourceLinks || []).map((source) => source.url))).size,
    };
  }, [dailyRadar, archiveItems]);

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetFilters = () => {
    setFilters({ search: "", topic: "All" });
    setSortBy("newest");
    setSelectedArchiveItem(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitMessage({ type: "", text: "" });

    if (!form.fullName.trim() || !form.email.trim()) {
      setSubmitMessage({
        type: "error",
        text: "Please add at least your name and email.",
      });
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        ...form,
        website: "artformyroom.com",
        sourcePage: "Creator Radar",
        requestType: "artist_daily_article_topic",
      };

      const response = await fetch(SUBSCRIPTION_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      let data = null;
      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { message: text || "Unexpected server response." };
      }

      if (!response.ok) {
        throw new Error(data.message || "Something went wrong.");
      }

      setSubmitMessage({
        type: "success",
        text: data.message || "Your Creator Radar topic request has been sent.",
      });

      setForm({
        fullName: "",
        email: "",
        artistName: "",
        topic: "Selling Art",
        notes: "",
      });
    } catch (error) {
      setSubmitMessage({
        type: "error",
        text: error.message || "Could not send your request right now.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <BackgroundDots />

      <PageContent>
        <Container maxWidth="xl" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 5, md: 7 } }}>
          <HeroShell elevation={0}>
            <HeroPattern />

            <HeroGlow
              sx={{
                width: 260,
                height: 260,
                left: -70,
                top: -70,
                background: alpha(BRAND.turquoiseLight, 0.32),
              }}
            />

            <HeroGlow
              sx={{
                width: 230,
                height: 230,
                right: -60,
                top: 80,
                background: alpha(BRAND.goldLight, 0.28),
                animationDelay: "1.2s",
              }}
            />

            <Box
              sx={{
                position: "relative",
                zIndex: 2,
                p: { xs: 3, sm: 4, md: 6 },
                minHeight: "500px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={7}>
                  <MotionBox
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Chip
                      icon={<AutoAwesomeIcon />}
                      label="Creator Radar by Art For My Room"
                      sx={{
                        mb: 2.4,
                        background: `linear-gradient(135deg, ${BRAND.turquoiseLight}, ${BRAND.goldLight})`,
                        color: BRAND.ink,
                        fontWeight: 950,
                        fontFamily: MONO_FONT,
                        "& .MuiChip-icon": { color: BRAND.ink },
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontWeight: 950,
                        lineHeight: 0.97,
                        letterSpacing: "-0.07em",
                        fontSize: { xs: "2.45rem", sm: "3.25rem", md: "5.1rem" },
                        mb: 2.4,
                      }}
                    >
                      One useful article every day, translated into artist action.
                    </Typography>

                    <Typography
                      sx={{
                        color: alpha("#FFFFFF", 0.80),
                        fontSize: { xs: "1rem", md: "1.15rem" },
                        lineHeight: 1.9,
                        maxWidth: 790,
                        mb: 3,
                      }}
                    >
                      Creator Radar finds a strong article of the day, summarises the
                      important points for painters and artists, links to the original
                      source, and saves the summary into the archive for discovery.
                    </Typography>

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <Chip
                        icon={<ArticleOutlinedIcon />}
                        label="Daily SEO post"
                        sx={{
                          backgroundColor: alpha("#FFFFFF", 0.12),
                          color: "#FFFFFF",
                          border: `1px solid ${alpha("#FFFFFF", 0.14)}`,
                          fontWeight: 850,
                          "& .MuiChip-icon": { color: "#FFFFFF" },
                        }}
                      />

                      <Chip
                        icon={<LibraryBooksRoundedIcon />}
                        label={`${stats.archive} archived summaries`}
                        sx={{
                          backgroundColor: alpha("#FFFFFF", 0.12),
                          color: "#FFFFFF",
                          border: `1px solid ${alpha("#FFFFFF", 0.14)}`,
                          fontWeight: 850,
                          "& .MuiChip-icon": { color: "#FFFFFF" },
                        }}
                      />

                      <Chip
                        icon={<SyncRoundedIcon />}
                        label={
                          feedUpdatedAt
                            ? `Updated ${formatDateTime(feedUpdatedAt)}`
                            : "Auto-refreshes while open"
                        }
                        sx={{
                          backgroundColor: alpha("#FFFFFF", 0.12),
                          color: "#FFFFFF",
                          border: `1px solid ${alpha("#FFFFFF", 0.14)}`,
                          fontWeight: 850,
                          "& .MuiChip-icon": { color: "#FFFFFF" },
                        }}
                      />
                    </Stack>
                  </MotionBox>
                </Grid>

                <Grid item xs={12} md={5}>
                  <DarkCard elevation={0} sx={{ p: 3 }}>
                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontWeight: 950,
                        fontSize: "1.55rem",
                        mb: 2,
                      }}
                    >
                      Today’s radar signal
                    </Typography>

                    <ArticleVisual>
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          zIndex: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 3,
                          textAlign: "center",
                        }}
                      >
                        <Stack spacing={1.2} alignItems="center">
                          <PaletteOutlinedIcon sx={{ fontSize: 58, color: BRAND.goldLight }} />
                          <Typography
                            sx={{
                              fontFamily: HEADING_FONT,
                              fontWeight: 950,
                              fontSize: "1.45rem",
                              lineHeight: 1.12,
                            }}
                          >
                            {dailyRadar.title}
                          </Typography>
                        </Stack>
                      </Box>
                    </ArticleVisual>

                    <Alert
                      severity={feedMessage ? "warning" : "success"}
                      sx={{
                        mt: 2,
                        borderRadius: 3,
                        backgroundColor: alpha(BRAND.turquoise, 0.10),
                        color: "#FFFFFF",
                        border: `1px solid ${alpha("#FFFFFF", 0.14)}`,
                        "& .MuiAlert-icon": { color: BRAND.goldLight },
                      }}
                    >
                      {feedLoading
                        ? "Loading today's Creator Radar..."
                        : feedMessage ||
                          "Today's summary is loaded. Source links appear underneath the summary."}
                    </Alert>
                  </DarkCard>
                </Grid>
              </Grid>
            </Box>
          </HeroShell>
        </Container>

        <Container maxWidth="xl" sx={{ pb: { xs: 6, md: 8 } }}>
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} lg={8}>
              <RadarArticleDetail item={dailyRadar} featured />
            </Grid>

            <Grid item xs={12} lg={4}>
              <GlassCard elevation={0} sx={{ p: 3, height: "100%" }}>
                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2 }}>
                  <TrendingUpRoundedIcon sx={{ color: BRAND.turquoiseDark }} />
                  <Typography
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontWeight: 950,
                      fontSize: "1.35rem",
                    }}
                  >
                    Why this helps Google discovery
                  </Typography>
                </Stack>

                <Typography sx={{ color: BRAND.muted, lineHeight: 1.85, mb: 2 }}>
                  Each day should create one indexable Creator Radar summary with
                  a date, topic, keyword set, source links, and structured article data.
                  This page updates meta tags and JSON-LD from today’s article.
                </Typography>

                <Stack spacing={1.3}>
                  {[
                    "SEO title changes with the daily article",
                    "Meta description uses the article summary",
                    "Keywords update from the daily topic",
                    "Source links keep the summary legally safer",
                    "Archive gives Google more useful internal content",
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={1.2} alignItems="flex-start">
                      <CheckCircleRoundedIcon
                        sx={{ color: BRAND.turquoiseDark, fontSize: 21, mt: "2px" }}
                      />
                      <Typography sx={{ color: BRAND.muted, lineHeight: 1.65 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Divider sx={{ my: 2.5, borderColor: BRAND.border }} />

                <Typography sx={{ color: BRAND.ink, fontWeight: 950, mb: 1 }}>
                  Best backend setup
                </Typography>

                <Typography sx={{ color: BRAND.muted, lineHeight: 1.75 }}>
                  For stronger SEO, the backend should also generate a unique URL
                  like <strong>/creator-radar/2026-05-30/article-slug</strong> and
                  include it automatically in your sitemap.
                </Typography>
              </GlassCard>
            </Grid>
          </Grid>

          <Box sx={{ mt: { xs: 5, md: 7 } }}>
            <Grid container spacing={4}>
              <Grid item xs={12} lg={4}>
                <GlassCard
                  elevation={0}
                  sx={{
                    p: 3,
                    position: { lg: "sticky" },
                    top: 24,
                  }}
                >
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2.4 }}>
                    <LibraryBooksRoundedIcon sx={{ color: BRAND.turquoiseDark }} />
                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontWeight: 950,
                        fontSize: "1.35rem",
                      }}
                    >
                      Creator Radar archive
                    </Typography>
                  </Stack>

                  <Stack spacing={2}>
                    <Field
                      fullWidth
                      label="Search archive"
                      value={filters.search}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, search: e.target.value }))
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchRoundedIcon sx={{ color: BRAND.muted }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Field
                      select
                      fullWidth
                      label="Topic"
                      value={filters.topic}
                      onChange={(e) =>
                        setFilters((prev) => ({ ...prev, topic: e.target.value }))
                      }
                    >
                      {topicOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Field>

                    <Field
                      select
                      fullWidth
                      label="Sort archive"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      {sortOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Field>

                    <SoftButton
                      startIcon={<RestartAltRoundedIcon />}
                      onClick={resetFilters}
                      fullWidth
                    >
                      Reset archive
                    </SoftButton>
                  </Stack>

                  <Paper
                    elevation={0}
                    sx={{
                      mt: 3,
                      p: 2.2,
                      borderRadius: 4,
                      border: `1px solid ${BRAND.border}`,
                      backgroundColor: BRAND.subtle,
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
                      <CheckCircleRoundedIcon sx={{ color: BRAND.turquoiseDark }} />
                      <Typography sx={{ color: BRAND.ink, fontWeight: 900 }}>
                        Unique daily summaries
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: BRAND.muted, lineHeight: 1.75 }}>
                      Duplicate articles are removed using title and source URL before
                      the archive is shown.
                    </Typography>
                  </Paper>
                </GlassCard>
              </Grid>

              <Grid item xs={12} lg={8}>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    sx={{
                      color: BRAND.ink,
                      fontFamily: HEADING_FONT,
                      fontWeight: 950,
                      fontSize: { xs: "1.9rem", md: "2.5rem" },
                      letterSpacing: "-0.045em",
                    }}
                  >
                    Open a past summary
                  </Typography>

                  <Typography sx={{ color: BRAND.muted, mt: 0.7, lineHeight: 1.75 }}>
                    {filteredArchive.length} archived Creator Radar summar
                    {filteredArchive.length === 1 ? "y" : "ies"} found.
                    {duplicatesRemoved > 0
                      ? ` ${duplicatesRemoved} duplicate article${duplicatesRemoved === 1 ? "" : "s"} removed.`
                      : " No duplicates detected."}
                  </Typography>
                </Box>

                {archiveDetail ? (
                  <Box sx={{ mb: 3 }}>
                    <RadarArticleDetail item={archiveDetail} />
                  </Box>
                ) : (
                  <GlassCard elevation={0} sx={{ p: 3, mb: 3 }}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <VisibilityRoundedIcon sx={{ color: BRAND.turquoiseDark }} />
                      <Box>
                        <Typography sx={{ fontWeight: 950, color: BRAND.ink }}>
                          Click an archive item to read its summary here.
                        </Typography>
                        <Typography sx={{ color: BRAND.muted, lineHeight: 1.7 }}>
                          Each archive entry should show the short summary, important
                          artist points, actions, SEO keywords and source links.
                        </Typography>
                      </Box>
                    </Stack>
                  </GlassCard>
                )}

                {filteredArchive.length === 0 ? (
                  <GlassCard elevation={0} sx={{ p: 4, textAlign: "center" }}>
                    <TravelExploreOutlinedIcon
                      sx={{ fontSize: 48, color: BRAND.turquoiseDark, mb: 1 }}
                    />
                    <Typography variant="h6" sx={{ color: BRAND.ink, fontWeight: 950, mb: 1 }}>
                      Fresh archive starts today
                    </Typography>
                    <Typography sx={{ color: BRAND.muted }}>
                      Past items can be deleted. Once the backend saves today’s radar,
                      future days will appear here.
                    </Typography>
                  </GlassCard>
                ) : (
                  <Grid container spacing={2}>
                    {filteredArchive.map((item) => (
                      <Grid item xs={12} key={makeRadarKey(item)}>
                        <ArchiveItem
                          item={item}
                          active={makeRadarKey(selectedArchiveItem || {}) === makeRadarKey(item)}
                          onClick={() => setSelectedArchiveItem(item)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Box>
        </Container>

        <Box
          sx={{
            py: { xs: 7, md: 9 },
            background:
              `linear-gradient(180deg, ${alpha("#FFFFFF", 0.1)} 0%, ${alpha(BRAND.turquoise, 0.09)} 100%)`,
            borderTop: `1px solid ${alpha(BRAND.turquoise, 0.12)}`,
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="stretch">
              <Grid item xs={12} md={5}>
                <DarkCard elevation={0} sx={{ p: 4, height: "100%" }}>
                  <Chip
                    icon={<EmailOutlinedIcon />}
                    label="Suggest a radar topic"
                    sx={{
                      mb: 2,
                      backgroundColor: alpha("#FFFFFF", 0.12),
                      color: "#FFFFFF",
                      border: `1px solid ${alpha("#FFFFFF", 0.16)}`,
                      fontWeight: 900,
                      "& .MuiChip-icon": { color: "#FFFFFF" },
                    }}
                  />

                  <Typography
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontWeight: 950,
                      fontSize: { xs: "1.9rem", md: "2.45rem" },
                      lineHeight: 1.08,
                      letterSpacing: "-0.045em",
                      mb: 1.6,
                    }}
                  >
                    Help shape tomorrow’s Creator Radar.
                  </Typography>

                  <Typography sx={{ color: alpha("#FFFFFF", 0.72), lineHeight: 1.85, mb: 2.4 }}>
                    Artists can suggest topics like selling original paintings,
                    pricing, collector psychology, gallery emails, AI tools,
                    room styling, or better painting photography.
                  </Typography>

                  <Stack spacing={1.5}>
                    {[
                      {
                        icon: <BrushOutlinedIcon sx={{ color: BRAND.goldLight }} />,
                        text: "Focused on painters and visual artists",
                      },
                      {
                        icon: <SellOutlinedIcon sx={{ color: BRAND.goldLight }} />,
                        text: "Useful for selling and presenting original artwork",
                      },
                      {
                        icon: <HomeWorkOutlinedIcon sx={{ color: BRAND.goldLight }} />,
                        text: "Connected to rooms, collectors and interior discovery",
                      },
                    ].map((item) => (
                      <Stack key={item.text} direction="row" spacing={1.2} alignItems="flex-start">
                        {item.icon}
                        <Typography sx={{ color: alpha("#FFFFFF", 0.76), lineHeight: 1.7 }}>
                          {item.text}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>

                  <Typography
                    sx={{
                      mt: 3,
                      color: alpha("#FFFFFF", 0.58),
                      fontSize: "0.9rem",
                      lineHeight: 1.7,
                    }}
                  >
                    Contact: {SITE_EMAIL}
                  </Typography>
                </DarkCard>
              </Grid>

              <Grid item xs={12} md={7}>
                <GlassCard
                  component="form"
                  onSubmit={handleSubmit}
                  elevation={0}
                  sx={{ p: 4 }}
                >
                  <Typography
                    sx={{
                      color: BRAND.ink,
                      fontFamily: HEADING_FONT,
                      fontWeight: 950,
                      fontSize: "1.65rem",
                      mb: 2.2,
                    }}
                  >
                    Suggest a useful daily article topic
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Field
                        fullWidth
                        label="Full name"
                        value={form.fullName}
                        onChange={(e) => handleFormChange("fullName", e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field
                        fullWidth
                        type="email"
                        label="Email"
                        value={form.email}
                        onChange={(e) => handleFormChange("email", e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field
                        fullWidth
                        label="Artist name or studio"
                        value={form.artistName}
                        onChange={(e) => handleFormChange("artistName", e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field
                        select
                        fullWidth
                        label="Topic"
                        value={form.topic}
                        onChange={(e) => handleFormChange("topic", e.target.value)}
                      >
                        {topicOptions
                          .filter((item) => item !== "All")
                          .map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                      </Field>
                    </Grid>

                    <Grid item xs={12}>
                      <Field
                        fullWidth
                        multiline
                        minRows={4}
                        label="What should Creator Radar cover?"
                        placeholder="Example: I want practical advice on pricing paintings for UK buyers, writing better artwork descriptions, or using room mockups to sell art."
                        value={form.notes}
                        onChange={(e) => handleFormChange("notes", e.target.value)}
                      />
                    </Grid>
                  </Grid>

                  {submitMessage.text && (
                    <Alert
                      severity={submitMessage.type === "success" ? "success" : "error"}
                      sx={{ mt: 2.4, borderRadius: 3 }}
                    >
                      {submitMessage.text}
                    </Alert>
                  )}

                  <GradientButton
                    type="submit"
                    disabled={submitting}
                    sx={{ mt: 3 }}
                  >
                    {submitting ? (
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <CircularProgress size={18} sx={{ color: "inherit" }} />
                        <span>Sending...</span>
                      </Stack>
                    ) : (
                      "Send topic suggestion"
                    )}
                  </GradientButton>
                </GlassCard>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </PageContent>
    </PageShell>
  );
}
